'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import {
  Button,
  IconButton,
  Textarea,
  Badge,
  Tooltip,
  Text,
} from '@terra/ui-design-system';
import { ClipboardIcon, MagicWand01Icon, Cancel01Icon } from '@terra/ui-icons';
import { useEffect, useRef } from 'react';
import { useDecoderStore } from '../../store/decoder-store';
import { detectEncodings } from './utils/operations';

export function DecoderInput() {
  const initialInput = useDecoderStore((state) => state.initialInput);
  const steps = useDecoderStore((state) => state.steps);
  const setInitialInput = useDecoderStore((state) => state.setInitialInput);
  const setSelectedOperation = useDecoderStore(
    (state) => state.setSelectedOperation,
  );
  const updateStepOperation = useDecoderStore(
    (state) => state.updateStepOperation,
  );
  const addStep = useDecoderStore((state) => state.addStep);
  const clearAllSteps = useDecoderStore((state) => state.clearAllSteps);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const element = textareaRef.current;

    if (!element) return;
    const persistedHeight = useDecoderStore.getState().inputHeight;

    if (persistedHeight) {
      element.style.height = `${persistedHeight}px`;
    }

    isInitialMount.current = false;
    const observer = new ResizeObserver((entries) => {
      if (isInitialMount.current) return;
      const entry = entries[0];

      if (entry) {
        useDecoderStore
          .getState()
          .setInputHeight(Math.round(entry.borderBoxSize[0].blockSize));
      }
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, []);
  const detections = detectEncodings(initialInput);
  const bestDetection = detections.length > 0 ? detections[0] : null;
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();

      setInitialInput(text);
    } catch (error) {
      console.error('Failed to paste from clipboard:', error);
    }
  };

  const handleSmartDecode = () => {
    if (!bestDetection) {
      return;
    }

    const detectedOperation = bestDetection.operation;

    setSelectedOperation(detectedOperation);
    const firstStep = steps[0];

    if (firstStep) {
      updateStepOperation({
        stepId: firstStep.id,
        operation: detectedOperation,
      });

      return;
    }

    addStep({ operation: detectedOperation });
  };

  return (
    <Box w="full">
      <HStack
        gap={1}
        px={2}
        py={1}
        bg="bg.subtle"
        borderBottomWidth="1px"
        borderColor="border.muted"
        justifyContent="space-between"
        alignItems="center"
        flexShrink={0}
      >
        <HStack gap={1} alignItems="center">
          <Text fontSize="xs" fontWeight="medium">
            Input
          </Text>
          {initialInput && (
            <Badge variant="fill" size="sm">
              {initialInput.length}
            </Badge>
          )}
        </HStack>
        <HStack gap={0} alignItems="center">
          <Tooltip
            title={
              bestDetection
                ? bestDetection.reason
                : 'No encoding detected in input'
            }
          >
            <Button
              variant="ghost"
              size="xs"
              onClick={handleSmartDecode}
              disabled={!bestDetection}
            >
              <MagicWand01Icon size={14} />
              Smart Decode
            </Button>
          </Tooltip>
          <Box w="1px" h={4} bg="border.muted" mx={1} />
          <Tooltip title="Paste from clipboard">
            <IconButton
              variant="ghost"
              size="xs"
              aria-label="Paste from clipboard"
              onClick={handlePaste}
            >
              <ClipboardIcon size={14} />
            </IconButton>
          </Tooltip>
          {initialInput && (
            <Tooltip title="Clear all">
              <IconButton
                variant="ghost"
                size="xs"
                aria-label="Clear input"
                onClick={() => clearAllSteps()}
              >
                <Cancel01Icon size={14} />
              </IconButton>
            </Tooltip>
          )}
        </HStack>
      </HStack>

      <Textarea
        ref={textareaRef}
        value={initialInput}
        onChange={(e) => setInitialInput(e.target.value)}
        placeholder="Paste or type encoded text…"
        minHeight="80px"
        fontFamily="monospace"
        fontSize="xs"
        bg="bg.canvas"
        borderWidth="0"
        borderRadius="0"
        px="2"
        py="2"
        w="full"
        resize="vertical"
        _focus={{ outline: 'none', boxShadow: 'none' }}
      />
    </Box>
  );
}
